import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, WifiOff } from "lucide-react";
import { enqueue } from "@/lib/offlineDb";

/**
 * Bulk tracking-number CSV import.
 * CSV format expected: header row "qr_code_string,tracking_number" (any case).
 * Drag-drop or click to choose. Preview before submitting.
 */
function parseCSV(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return { header: [], rows: [], errors: ["Empty file"] };
  const splitRow = (l) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < l.length; i++) {
      const ch = l[i];
      if (inQ) {
        if (ch === '"' && l[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQ = false; }
        else cur += ch;
      } else {
        if (ch === ',') { out.push(cur); cur = ""; }
        else if (ch === '"') { inQ = true; }
        else cur += ch;
      }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const header = splitRow(lines[0]).map((h) => h.toLowerCase());
  const qrIdx = header.findIndex((h) => h === "qr_code_string" || h === "qr");
  const tnIdx = header.findIndex((h) => h === "tracking_number" || h === "tracking");
  const errors = [];
  if (qrIdx < 0) errors.push('Missing "qr_code_string" column');
  if (tnIdx < 0) errors.push('Missing "tracking_number" column');
  const rows = [];
  if (errors.length === 0) {
    for (let i = 1; i < lines.length; i++) {
      const cols = splitRow(lines[i]);
      const qr = cols[qrIdx]; const tn = cols[tnIdx];
      if (!qr || !tn) { errors.push(`Line ${i + 1}: missing value`); continue; }
      rows.push({ qr_code_string: qr, tracking_number: tn });
    }
  }
  return { header, rows, errors };
}

export default function DispatchTrackingImport() {
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const parsed = useMemo(() => (text ? parseCSV(text) : null), [text]);

  const onFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("CSV must be < 5MB"); return; }
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const submit = async () => {
    if (!parsed || parsed.errors.length > 0 || parsed.rows.length === 0) return;
    setSubmitting(true);
    // Offline path: enqueue the entire batch as a single job.
    if (!navigator.onLine) {
      try {
        await enqueue("tracking", parsed.rows, { row_count: parsed.rows.length, filename: file?.name });
        setResult({ updated: 0, submitted: parsed.rows.length, queued: true });
        toast.success(`Queued ${parsed.rows.length} rows · will sync when online`);
      } catch (e) { toast.error("Queue failed: " + e.message); }
      finally { setSubmitting(false); }
      return;
    }
    try {
      const { data } = await api.post("/dispatch/tracking-import", parsed.rows);
      setResult(data);
      toast.success(`Updated ${data.updated} of ${data.submitted} rows`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  return (
    <div data-testid="dispatch-tracking-import" className="mx-auto max-w-3xl">
      <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
        <Link to="/dispatch"><ArrowLeft size={14} className="mr-1" />Back to Dispatch</Link>
      </Button>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Bulk Tracking-Number Import</h1>
      <p className="mt-1 max-w-xl text-sm text-slate-500">After India Post issues tracking numbers (Speed/Registered Post), upload their CSV here to map each tracking number back to its Dispatch QR.</p>
      {!online && (
        <div data-testid="trk-offline-banner" className="mt-3 flex items-center gap-2 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <WifiOff size={14} />
          <span>Offline · this batch will be queued in IndexedDB and posted automatically when the network returns.</span>
        </div>
      )}

      <div className="mt-6 rounded-sm border border-slate-200 bg-white p-5">
        <div className="mb-3 rounded-sm bg-slate-50 px-3 py-2 text-xs text-slate-700">
          Required columns: <span className="font-mono font-semibold">qr_code_string</span>, <span className="font-mono font-semibold">tracking_number</span> (case-insensitive). Other columns are ignored.
        </div>

        <label
          htmlFor="csv-input"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer?.files); }}
          data-testid="csv-dropzone"
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed p-10 text-center transition-colors ${dragOver ? "border-[#002FA7] bg-indigo-50" : "border-slate-300 bg-slate-50 hover:border-[#002FA7]"}`}
        >
          <FileSpreadsheet className="text-slate-400" size={36} />
          <div className="text-sm text-slate-700">{file ? <span className="font-medium">{file.name}</span> : <>Drop a <span className="font-mono">.csv</span> here, or click to choose</>}</div>
          <div className="text-xs text-slate-500">{file ? `${(file.size / 1024).toFixed(1)} KB` : "Max 5MB"}</div>
          <input id="csv-input" type="file" accept=".csv,text/csv" onChange={(e) => onFiles(e.target.files)} className="hidden" data-testid="csv-file-input" />
        </label>

        {parsed && (
          <div className="mt-5">
            {parsed.errors.length > 0 ? (
              <div className="rounded-sm border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertCircle size={14} />Cannot parse:</div>
                <ul className="list-disc pl-5 text-xs">{parsed.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            ) : (
              <>
                <div className="mb-2 text-sm text-slate-700"><span className="font-semibold">{parsed.rows.length}</span> valid row{parsed.rows.length !== 1 ? "s" : ""} ready.</div>
                <div className="max-h-64 overflow-auto rounded-sm border border-slate-200" data-testid="csv-preview">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 text-slate-500"><tr><th className="px-3 py-2 text-left font-semibold">QR</th><th className="px-3 py-2 text-left font-semibold">Tracking Number</th></tr></thead>
                    <tbody>
                      {parsed.rows.slice(0, 200).map((r, i) => (
                        <tr key={i} className="border-t border-slate-100"><td className="px-3 py-1.5 font-mono">{r.qr_code_string}</td><td className="px-3 py-1.5 font-mono">{r.tracking_number}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.rows.length > 200 && <div className="bg-slate-50 px-3 py-2 text-xs text-slate-500">…and {parsed.rows.length - 200} more</div>}
                </div>
                <div className="mt-3 flex justify-end">
                  <Button onClick={submit} disabled={submitting || parsed.rows.length === 0} data-testid="csv-submit-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
                    <Upload size={14} className="mr-1.5" />{submitting ? "Importing…" : `Import ${parsed.rows.length} row${parsed.rows.length !== 1 ? "s" : ""}`}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {result && (
          <div className="mt-5 flex items-center gap-2 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="csv-result">
            <CheckCircle2 size={16} /> {result.queued
              ? <>Queued <span className="font-semibold">{result.submitted}</span> rows for sync when online.</>
              : <>Updated <span className="font-semibold">{result.updated}</span> of <span className="font-semibold">{result.submitted}</span> rows.</>}
          </div>
        )}
      </div>
    </div>
  );
}
